import { useEffect, useState } from 'react';

/**
 * Matches Tailwind's `md` breakpoint (768px). Anything below is treated as mobile.
 * Kept in sync with the `md:` prefix used throughout the responsive layout.
 */
const MOBILE_QUERY = '(max-width: 767px)';

/**
 * useIsMobile
 *
 * Returns `true` when the viewport is below Tailwind's `md` breakpoint.
 * Use ONLY where render logic must branch in JS (e.g. rendering a card list vs.
 * a table to avoid mounting both). Prefer CSS `md:` utilities everywhere else.
 *
 * SSR-safe (falls back to `false` when `window` is unavailable) and cleans up
 * its listener on unmount.
 */
export default function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return false;
    }
    return window.matchMedia(MOBILE_QUERY).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return;
    }

    const mql = window.matchMedia(MOBILE_QUERY);
    const handleChange = (event: MediaQueryListEvent) => setIsMobile(event.matches);

    // Sync immediately in case the viewport changed between the initial render
    // and this effect running.
    setIsMobile(mql.matches);
    mql.addEventListener('change', handleChange);

    return () => mql.removeEventListener('change', handleChange);
  }, []);

  return isMobile;
}
